// Lazy-loader + thin wrappers around the AWS WAF JS CAPTCHA + intelligent-threat SDKs.
// Set VITE_AWS_WAF_INTEGRATION_URL and VITE_AWS_WAF_API_KEY at build time.

declare global {
  interface Window {
    AwsWafIntegration?: {
      fetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
      getToken: () => Promise<string>;
    };
    AwsWafCaptcha?: {
      renderCaptcha: (
        container: HTMLElement,
        opts: {
          apiKey: string;
          onSuccess: (wafToken: string) => void;
          onError?: (err: unknown) => void;
          onLoad?: () => void;
          onPuzzleCorrect?: (wafToken: string) => void;
          onPuzzleIncorrect?: () => void;
          onPuzzleTimeout?: () => void;
          dynamicWidth?: boolean;
          skipTitle?: boolean;
          defaultLocale?: string;
        },
      ) => void;
    };
  }
}

const INTEGRATION_URL = (import.meta.env.VITE_AWS_WAF_INTEGRATION_URL ?? "").replace(/\/+$/, "");
const API_KEY = import.meta.env.VITE_AWS_WAF_API_KEY ?? "";

export function isAwsWafEnabled(): boolean {
  return Boolean(INTEGRATION_URL && API_KEY);
}

export function getApiKey(): string {
  return API_KEY;
}

let loadPromise: Promise<void> | null = null;

export function loadAwsWafSdk(): Promise<void> {
  if (typeof window === "undefined") return Promise.reject(new Error("not in browser"));
  if (!INTEGRATION_URL) return Promise.reject(new Error("aws waf integration url not configured"));
  if (window.AwsWafCaptcha && window.AwsWafIntegration) return Promise.resolve();
  if (loadPromise) return loadPromise;

  const src = `${INTEGRATION_URL}/jsapi.js`;
  loadPromise = new Promise<void>((resolve, reject) => {
    const onReady = () => {
      // jsapi.js loads both globals (CAPTCHA + intelligent threat).
      if (window.AwsWafCaptcha) resolve();
      else reject(new Error("aws waf jsapi.js loaded but AwsWafCaptcha missing"));
    };

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
    if (existing) {
      existing.addEventListener("load", onReady, { once: true });
      existing.addEventListener("error", () => reject(new Error("aws waf script failed")));
      if (window.AwsWafCaptcha) resolve();
      return;
    }
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.defer = true;
    s.addEventListener("load", onReady, { once: true });
    s.addEventListener("error", () => reject(new Error("aws waf script failed")));
    document.head.appendChild(s);
  });
  return loadPromise;
}

/**
 * Submits a request, letting the WAF intelligent-threat SDK attach the cached
 * token if the user has solved the puzzle. Falls back to plain fetch when the
 * integration isn't configured (local dev with empty env).
 */
export async function wafFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  if (!isAwsWafEnabled()) return fetch(input, init);
  await loadAwsWafSdk();
  if (!window.AwsWafIntegration) return fetch(input, init);
  return window.AwsWafIntegration.fetch(input, init);
}
